import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

/**
 * useViewInHistory(key, view, setView) → { pushView(next) }
 * readHistoryView(key, fallback, { location, navigationType })
 *
 * THE answer to "what does the browser's Back button do to a page that has
 * several views or tabs": each HISTORY ENTRY remembers what it was showing.
 * Owner 2026-10-13: "If I click on a task or a gag it takes me to the breakdown
 * list. When I click back I want to go back to the last view (gantt chart
 * etc)", then all of the site's list pages. Until then a jump between views was
 * a state change with no history entry, so Back left the page altogether; and
 * coming Back to a page from somewhere else reopened it on its default.
 *
 *   view           a string, or a small plain object ({ tab, status }) when a
 *                  page has more than one thing to remember. Compared by value.
 *   the stamp      whenever `view` changes, the CURRENT entry is stamped with it.
 *   pushView(next) for a JUMP the user would expect Back to undo (Labour → the
 *                  gag in the Table): pushes a NEW entry at the same URL
 *                  carrying `next`. The caller still sets its own state.
 *   the restore    on a Back/Forward (`POP`), an entry's stamped view is handed
 *                  to `setView`. 🔑 `setView` must VALIDATE it: a stamp outlives
 *                  a release, so a tab id may have been retired since.
 *
 * 🔑 A FRESH VISIT, A LINK AND A RELOAD ARE NOT RESTORED, on purpose: a page that
 * "always opens in Table view" (the Breakdown's ruling), or on its saved
 * preference, still does. Only a Back/Forward inside this running app restores,
 * which is what `live` tracks: a module variable, so a reload starts it false
 * again even though the browser kept the entry's state.
 *
 * 🪤 THE STAMP IS A RAW `history.replaceState`, NOT A ROUTER NAVIGATION, and that
 * is load-bearing. Several pages tidy their own URL in an effect
 * (`useDeepLinkParam` deletes `?gag=` with `setSearchParams(…, { replace })`). A
 * router REPLACE issued here in the same commit carries the STALE search string
 * and puts the parameter back, so the link fires twice; and theirs carries no
 * `state`, so it wipes the stamp. A raw write touches only this hook's own key
 * under `history.state.usr` (where react-router keeps an entry's state and
 * reads it back on a POP), never the URL, and is simply re-applied after any
 * navigation (`location.key`). It stores nothing outside the history entry.
 *
 * ⚠️ A memory router has no `window.history`, so a test must use a BROWSER
 * router to see a restore. jsdom has no real Back button, bfcache or reload:
 * that a browser's Back lands on the right view is a BROWSER check.
 */
let live = false;

// What this running app knows about the history entry it is on, worked out ONCE
// per entry by whichever hook instance sees it first (a page runs several):
//   delta  how many entries the navigation moved (-1 a Back, +1 a Forward), from
//          the router's own `idx`; 0 when it cannot be told
let seen = { key: null, delta: 0 };
let lastIdx = null;
// THE LOAD: true from the first hook effect of a freshly loaded app until that
// commit's effects have all run. The entry shown then (a reload) is never
// restored and is always re-stamped.
// 🪤 Module state and not a ref: `React.StrictMode` runs every mount effect
// twice and keeps the refs, so a "first run" ref is spent by the rehearsal and a
// reload restored under `npm start` (review 2026-10-13).
// 🪤 And a property of the LOAD, not of the entry: tied to the entry's key, the
// page never restored on it again, so "load the page, open a row, go elsewhere,
// press Back" reopened nothing (browser-measured the same day).
let loading = false;
// The entry a Back/Forward landed on while the page had an unsaved edit: the
// navigation is being undone, so nothing restores from it or stamps it.
let held = null;
// The mounted page's own "is an edit unsaved / ask, then continue" pair.
let guard = null;

function observe(locationKey) {
  if (seen.key === locationKey) return seen;
  const idx = window.history.state ? window.history.state.idx : null;
  const known = Number.isInteger(idx) && Number.isInteger(lastIdx);
  seen = { key: locationKey, delta: known ? idx - lastIdx : 0 };
  if (Number.isInteger(idx)) lastIdx = idx;
  if (held !== locationKey) held = null;
  if (!live) {
    live = true;
    loading = true;
    // A later task: after this commit's effects, StrictMode's second run included.
    setTimeout(() => { loading = false; }, 0);
  }
  return seen;
}

function holdNavigation(entry, navigate, continueWhenClean) {
  if (held === entry.key) return;
  held = entry.key;
  // Direction unknown: nothing can be undone, so nothing is restored either.
  if (!entry.delta) return;
  navigate(-entry.delta);
  continueWhenClean(() => navigate(entry.delta));
}

const stateKey = (key) => `view:${key}`;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** For a `useState` initialiser: the stamped view when this mount is a Back or
 *  Forward inside the running app, else `fallback`. The caller validates it. */
export function readHistoryView(key, fallback, { location, navigationType } = {}) {
  if (!live || navigationType !== 'POP') return fallback;
  const v = location?.state?.[stateKey(key)];
  return v == null ? fallback : v;
}

/**
 * useHistoryRestoreGuard(dirty, continueWhenClean)
 *
 * 🚨 EVERY PAGE THAT USES THIS FILE AND HAS AN INLINE EDITOR CALLS THIS ONCE,
 * unless a dirty row is PINNED so that no tab or view change can unmount it
 * (Department Orders, the hires Table). A Back/Forward between two entries of
 * the SAME page changes no pathname, so "Leave without saving?" does not fire
 * (`useBlocker` only sees a pathname change), and a restore would switch the
 * tab or view under the edit and lose it in silence. Reached three times on
 * 2026-10-13: the Breakdown (Labour, a gag, an edit, Back), then My Tasks and
 * My Orders through a bell link, which pushes `?task=` / `?order=` on the same
 * path.
 *
 * With `dirty` true, a Back/Forward that would restore ANYTHING is first UNDONE
 * (the browser returns to the entry the user was on), then the page's own
 * three-way prompt is raised through `continueWhenClean` with "repeat the
 * navigation" as what it continues to. Keep editing leaves everything where it
 * was; Save / Discard perform the Back, and the restore then runs clean, every
 * key at once. ONE hold per navigation however many keys the page keeps: the
 * continuation has one slot, and two holds dropped the Back (review F2).
 * The direction comes from the router's `idx`, so a Forward is undone with a
 * Back and not the reverse (review F3).
 */
export function useHistoryRestoreGuard(dirty, continueWhenClean) {
  const latest = useRef(null);
  latest.current = { dirty, continueWhenClean };
  useEffect(() => {
    guard = latest;
    // Leaving also ends any hold in flight: a later page must not inherit it.
    return () => { if (guard === latest) { guard = null; held = null; } };
  }, []);
}

/**
 * useHistoryState(key, fallback, isValid) → [value, setValue, pushView]
 *
 * The drop-in for a `useState` that holds a page's TAB or VIEW: the same value,
 * the same setter, plus the Back behaviour above. `fallback` (a value, or a
 * function for a lazy one) is what a fresh visit opens on. `isValid(v)` guards
 * BOTH the first read and every restore, so a retired id falls back instead of
 * stranding the user on an empty tab.
 */
export function useHistoryState(key, fallback, isValid = () => true, restore = null) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [value, setValue] = useState(() => {
    const stamped = readHistoryView(key, undefined, { location, navigationType });
    if (stamped !== undefined && isValid(stamped)) return stamped;
    return typeof fallback === 'function' ? fallback() : fallback;
  });
  // `restore`: what a Back/Forward calls instead of the plain setter, for a
  // page whose tab change does more than set the tab (the Breakdown closes its
  // open rows). It is NOT the unsaved-edit guard: that is
  // `useHistoryRestoreGuard`, above, and a page with an editor owes it.
  const { pushView } = useViewInHistory(key, value, (v) => { if (isValid(v)) (restore || setValue)(v); });
  return [value, setValue, pushView];
}

/**
 * useOpenRowInHistory(key, openId, requestOpen)
 *
 * The same rule for the ROW a list page has expanded (owner 2026-10-13: "yes
 * build the clean-row reopen"): the history entry remembers which order, gag or
 * hire was open, and a Back/Forward onto it asks the page to open that row
 * again. `openId` is the id of the one open row, or null. `requestOpen(id)` is
 * the page's OWN deep-link request (what `?order=` / `?gag=` / `?hire=` call),
 * so finding the row's tab and page, and saying so when it has gone, is the
 * page's existing behaviour and is not rebuilt here.
 *
 * 🔑 CLEAN ROWS ONLY. It never restores a draft. Leaving the page over an
 * unsaved row asks first ("Leave without saving?"); a Back that stays ON the
 * page does not, which is what `useHistoryRestoreGuard` is for.
 * A closed row stamps null, which restores nothing: Back never CLOSES a row.
 */
export function useOpenRowInHistory(key, openId, requestOpen) {
  const id = Number.isInteger(openId) && openId > 0 ? openId : null;
  useViewInHistory(key, id, (stamped) => {
    if (Number.isInteger(stamped) && stamped > 0) requestOpen(stamped);
  });
}

export default function useViewInHistory(key, view, setView) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const k = stateKey(key);
  const viewJson = JSON.stringify(view);

  // A Back/Forward onto an entry that remembers something else: put it back.
  // 🪤 A RELOAD is a POP too, and the browser keeps the entry's state across it,
  // so the entry the app loaded on is never restored (browser-measured
  // 2026-10-13: without this a reload on Timeline reopened on Timeline, against
  // "always opens on Table").
  useEffect(() => {
    const entry = observe(location.key);
    if (loading || navigationType !== 'POP') return;
    const stamped = location.state?.[k];
    if (stamped == null || same(stamped, view)) return;
    const g = guard && guard.current;
    if (g && g.dirty) { holdNavigation(entry, navigate, g.continueWhenClean); return; }
    if (held === entry.key) return;
    setView(stamped);
    // Only a new history entry may restore; `view` changing must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Stamp the current entry with what it is showing (see the 🪤 above).
  // 🪤 NOT ON LANDING ON AN ENTRY THAT ALREADY REMEMBERS SOMETHING. A Back lands
  // with the page still showing the view it LEFT; stamping then would overwrite
  // what the entry remembers before the restore has applied it. When the
  // restore is immediate that heals a render later, but a restore the page
  // DECLINES would leave the entry remembering the wrong view for good
  // (browser-measured 2026-10-13: the next Back then did nothing).
  // So a landing leaves the stamp alone; the next real change of `view` writes it.
  // The one landing that must stamp is a cold load (a reload): its stale stamp
  // is being ignored on purpose and has to be replaced by what is now shown.
  // An entry whose navigation is being undone (`held`) is never stamped at all.
  const stampedKey = useRef(null);
  useEffect(() => {
    observe(location.key);
    const landed = stampedKey.current !== location.key;
    stampedKey.current = location.key;
    try {
      const heldState = window.history.state && window.history.state.usr;
      const usr = heldState && typeof heldState === 'object' ? heldState : {};
      if (same(usr[k], view)) return;
      if (held === location.key) return;
      if (landed && !loading && navigationType === 'POP' && usr[k] != null) return;
      // 🔑 The router's own `idx` and `key` ride along untouched: the spread comes
      // FIRST and only `usr` is replaced. `useUnsavedChangesGuard.sites` §5 pins
      // that shape; wiping them would leave Back onto this entry unblockable.
      window.history.replaceState({ ...window.history.state, usr: { ...usr, [k]: view } }, '');
    } catch { /* a convenience: without it Back behaves as it always did */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, viewJson, location.key]);

  const pushView = useCallback((next) => {
    navigate(
      { pathname: location.pathname, search: location.search },
      { state: { ...(location.state && typeof location.state === 'object' ? location.state : {}), [k]: next } },
    );
  }, [navigate, location.pathname, location.search, location.state, k]);

  return { pushView };
}
